import { baseApi } from '@app/baseApi';

import type {
  Comment,
  ConnectedExperience,
  CreateCommentRequest,
  CreateConnectedExperienceRequest,
  Deployment,
} from '@/types';

import {
  transformCommentResponse,
  transformConnectedExperienceResponse,
  transformCreateCommentRequest,
  transformCreateConnectedExperienceRequest,
} from './connectedExperiences.mappers';

export const connectedExperiencesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get connected experiences for an app
    getConnectedExperiences: builder.query<
      ConnectedExperience[],
      { tenantId: string; appId: string }
    >({
      query: ({ tenantId, appId }) => ({
        url: `${tenantId}/${appId}/connectedexperiences`,
        endpoint: 'transformer',
      }),
    }),

    // Create connected experience
    // VERIFIED against transformer_api/structs/connectedExperience.go
    // Backend expects camelCase: { id, name, recipeBaseIds, wizardGuids, type, reports }
    createConnectedExperience: builder.mutation<
      ConnectedExperience,
      { tenantId: string; appId: string; data: CreateConnectedExperienceRequest }
    >({
      query: ({ tenantId, appId, data }) => ({
        url: `${tenantId}/${appId}/connectedexperience`,
        endpoint: 'transformer',
        method: 'POST',
        body: transformCreateConnectedExperienceRequest(data),
      }),
      transformResponse: transformConnectedExperienceResponse,
    }),

    // Delete connected experience
    deleteConnectedExperience: builder.mutation<
      void,
      { tenantId: string; appId: string; expId: string }
    >({
      query: ({ tenantId, appId, expId }) => ({
        url: `${tenantId}/${appId}/connectedexperience/${expId}`,
        endpoint: 'transformer',
        method: 'DELETE',
      }),
    }),

    // Get connected experience comments
    getConnectedExperienceComments: builder.query<
      Comment[],
      { tenantId: string; appId: string; expId: string }
    >({
      query: ({ tenantId, appId, expId }) => ({
        url: `${tenantId}/${appId}/connectedexperience/${expId}/comments`,
        endpoint: 'transformer',
      }),
    }),

    // Add comment to connected experience
    // VERIFIED against transformer_api/structs/connectedExperience.go:L30-39
    // Backend expects camelCase: { username, userId, comment }
    addConnectedExperienceComment: builder.mutation<
      Comment,
      { tenantId: string; appId: string; expId: string; data: CreateCommentRequest }
    >({
      query: ({ tenantId, appId, expId, data }) => ({
        url: `${tenantId}/${appId}/connectedexperience/${expId}/comment`,
        endpoint: 'transformer',
        method: 'POST',
        body: transformCreateCommentRequest(data),
      }),
      transformResponse: transformCommentResponse,
    }),

    // Delete comment from connected experience
    deleteConnectedExperienceComment: builder.mutation<
      void,
      { tenantId: string; appId: string; expId: string; commentId: string }
    >({
      query: ({ tenantId, appId, expId, commentId }) => ({
        url: `${tenantId}/${appId}/connectedexperience/${expId}/comment/${commentId}`,
        endpoint: 'transformer',
        method: 'DELETE',
      }),
    }),

    // Get deployments for an app
    getDeployments: builder.query<Deployment[], { tenantId: string; appId: string }>({
      query: ({ tenantId, appId }) => ({
        url: `${tenantId}/${appId}/deployments`,
        endpoint: 'transformer',
      }),
    }),
  }),
});

export const {
  useGetConnectedExperiencesQuery,
  useCreateConnectedExperienceMutation,
  useDeleteConnectedExperienceMutation,
  useGetConnectedExperienceCommentsQuery,
  useAddConnectedExperienceCommentMutation,
  useDeleteConnectedExperienceCommentMutation,
  useGetDeploymentsQuery,
} = connectedExperiencesApi;
